import { useEffect, useRef, useState } from "react"
import {
  Bot,
  ChevronDown,
  ChevronRight,
  Loader2,
  RefreshCw,
  Send,
  Trash2,
} from "lucide-react"
import { Link } from "react-router-dom"
import { getAllPrompts, sandboxChat, type SandboxMessage } from "@/lib/api"
import { toast } from "sonner"

interface Turn {
  role: "user" | "assistant"
  content: string
  raw?: any
  error?: string
}

function extractText(raw: any): string {
  const blocks = Array.isArray(raw?.content) ? raw.content : []
  return blocks
    .filter((b: any) => b?.type === "text")
    .map((b: any) => b.text)
    .join("")
    .trim()
}

function RawJson({ data }: { data: any }) {
  const [open, setOpen] = useState(false)
  const usage = data?.usage
  return (
    <div className="mt-2 border-t pt-2">
      <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
        {data?.stop_reason && <span>stop: {data.stop_reason}</span>}
        {usage && (
          <span>
            tokens: {usage.input_tokens} in / {usage.output_tokens} out
          </span>
        )}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="ml-auto inline-flex items-center gap-0.5 hover:text-foreground"
        >
          {open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
          raw JSON
        </button>
      </div>
      {open && (
        <pre className="mt-1 max-h-72 overflow-auto rounded bg-foreground p-2 text-[10px] text-background">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  )
}

export function AgentSandboxPage() {
  const [systemPrompt, setSystemPrompt] = useState("")
  const [isLoadingPrompt, setIsLoadingPrompt] = useState(false)
  const [draft, setDraft] = useState("")
  const [turns, setTurns] = useState<Turn[]>([])
  const [sending, setSending] = useState(false)
  const endRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })
  }, [turns, sending])

  const loadCurrentChatPrompt = async () => {
    setIsLoadingPrompt(true)
    try {
      const prompts = await getAllPrompts()
      const chat = prompts.chat
      setSystemPrompt(chat?.override_text || chat?.text || "")
      toast.success("Loaded the live chat system prompt")
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load prompt")
    } finally {
      setIsLoadingPrompt(false)
    }
  }

  const send = async () => {
    const text = draft.trim()
    if (!text || sending) return
    if (!systemPrompt.trim()) {
      toast.error("Add a system prompt first")
      return
    }
    const history: SandboxMessage[] = [
      ...turns.filter((t) => !t.error).map((t) => ({ role: t.role, content: t.content })),
      { role: "user" as const, content: text },
    ]
    setDraft("")
    setTurns((prev) => [...prev, { role: "user", content: text }])
    setSending(true)
    try {
      const raw = await sandboxChat(systemPrompt, history)
      const replyText = extractText(raw) || "(empty response)"
      setTurns((prev) => [...prev, { role: "assistant", content: replyText, raw }])
    } catch (err) {
      const message = err instanceof Error ? err.message : "Sandbox call failed"
      setTurns((prev) => [...prev, { role: "assistant", content: "", error: message }])
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Bot className="size-6 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-bold">Agent Sandbox</h1>
          <p className="text-xs text-muted-foreground">
            Try a custom system prompt / personality against the coach in isolation. Nothing
            here is saved and no real user is touched — to ship a prompt for real, use the{" "}
            <Link to="/prompts" className="underline hover:text-foreground">
              Prompts
            </Link>{" "}
            page.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,380px)_minmax(0,1fr)] gap-6">
        {/* System prompt panel */}
        <div className="space-y-3 xl:sticky xl:top-4 xl:max-h-[calc(100vh-2rem)] xl:overflow-y-auto">
          <div className="rounded-lg border bg-card p-3 space-y-2">
            <div className="flex items-center justify-between">
              <label htmlFor="sandbox-system-prompt" className="text-xs font-semibold">
                System prompt
              </label>
              <button
                type="button"
                onClick={loadCurrentChatPrompt}
                disabled={isLoadingPrompt}
                className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-50"
              >
                <RefreshCw className={`size-3 ${isLoadingPrompt ? "animate-spin" : ""}`} />
                Load current chat prompt
              </button>
            </div>
            <textarea
              id="sandbox-system-prompt"
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              rows={20}
              placeholder="Paste or write a system prompt / personality to test…"
              className="w-full font-mono text-xs px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary bg-background resize-y"
            />
            <div className="text-[10px] text-muted-foreground">
              {systemPrompt.length.toLocaleString()} chars
            </div>
          </div>
        </div>

        {/* Conversation */}
        <div className="space-y-3">
          {turns.length === 0 && !sending && (
            <div className="rounded-lg border border-dashed bg-card p-8 text-center text-sm text-muted-foreground">
              Write a system prompt on the left, then send a message to see how the agent
              responds — the raw API response is included under each reply.
            </div>
          )}

          {turns.map((t, i) =>
            t.role === "user" ? (
              <div key={i} className="flex justify-end">
                <div className="max-w-[85%] rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground">
                  {t.content}
                </div>
              </div>
            ) : (
              <div key={i} className="rounded-lg border bg-card p-3">
                {t.error ? (
                  <div className="text-sm text-red-800 dark:text-red-300">{t.error}</div>
                ) : (
                  <>
                    <p className="whitespace-pre-wrap text-sm">{t.content}</p>
                    <RawJson data={t.raw} />
                  </>
                )}
              </div>
            ),
          )}

          {sending && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Calling the agent…
            </div>
          )}
          <div ref={endRef} />

          {turns.length > 0 && (
            <button
              type="button"
              onClick={() => setTurns([])}
              className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] text-muted-foreground hover:text-foreground"
            >
              <Trash2 className="size-3" /> Clear conversation
            </button>
          )}

          {/* Composer */}
          <div className="sticky bottom-4 z-10 flex items-end gap-2 rounded-lg border bg-card p-2 shadow-lg">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault()
                  void send()
                }
              }}
              rows={2}
              placeholder="Message the agent… (Enter to send, Shift+Enter for a new line)"
              className="flex-1 resize-none rounded-md border bg-background px-2 py-1.5 text-sm"
            />
            <button
              type="button"
              onClick={() => void send()}
              disabled={sending || !draft.trim()}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {sending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Send className="size-4" />
              )}
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
